import { type CalendarEvent } from '@/types/calendar';
import { getMeetLink, getMeetingType, isEventNow, isEventUpcoming } from '@/lib/calendar';
import { Icon } from '@iconify/react';
import { Clock, Users } from 'lucide-react';
import { format } from 'date-fns';
import { IconButton } from '@/components/ui/icon-button';

interface MeetingCardProps {
  event: CalendarEvent;
  onJoinMeeting: (url: string) => void;
}

export function MeetingCard({ event, onJoinMeeting }: MeetingCardProps) {
  const meetLink = getMeetLink(event);
  const meetingType = getMeetingType(event);
  const isNow = isEventNow(event);
  const isUpcoming = isEventUpcoming(event);

  const startTime = new Date(event.start.dateTime);
  const endTime = new Date(event.end.dateTime);

  const formatTime = (date: Date) => format(date, 'h:mm a');

  const getMeetingIcon = () => {
    switch (meetingType) {
      case 'google-meet':
        return <Icon icon="logos:google-meet" className="h-5 w-5" />;
      case 'zoom':
        return <Icon icon="logos:zoom-icon" className="h-5 w-5" />;
      case 'teams':
        return <Icon icon="logos:microsoft-teams" className="h-5 w-5" />;
      default:
        return <Icon icon="mdi:video" className="h-5 w-5" />;
    }
  };

  const getMeetingLabel = () => {
    switch (meetingType) {
      case 'google-meet':
        return 'Join Meet';
      case 'zoom':
        return 'Join Zoom';
      case 'teams':
        return 'Join Teams';
      default:
        return 'Join Meeting';
    }
  };

  return (
    <div
      className={`border rounded bg-surface-1 p-6 transition-all hover:shadow-md block w-full ${
        isNow ? 'border-primary border-2' : 'border-base-300'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-medium text-content-1">
              {event.summary || 'Untitled Meeting'}
            </h3>
            {isNow && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-success text-success-foreground">
                Happening now
              </span>
            )}
            {!isNow && isUpcoming && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                Starting soon
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-content-3" />
            <span className="text-sm text-content-3">
              {formatTime(startTime)} - {formatTime(endTime)}
            </span>
          </div>

          {event.description && (
            <div
              className="mb-3 line-clamp-2 text-sm text-content-2"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          )}

          <div className="flex items-center gap-4">
            {event.organizer && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-content-3" />
                <span className="text-xs text-content-3">
                  {event.organizer.displayName || event.organizer.email}
                </span>
              </div>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <span className="text-xs text-content-3">
                {event.attendees.length} guest{event.attendees.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {meetLink && (
          <IconButton
            onClick={() => onJoinMeeting(meetLink)}
            variant={isNow ? 'primary' : 'outline'}
            className="px-6 py-3 text-sm"
          >
            {getMeetingIcon()}
            {getMeetingLabel()}
          </IconButton>
        )}
      </div>
    </div>
  );
}
